import {
    AuthenticationSvc,
    USER_UPDATE_SUCCESS
} from '../services/AuthenticationSvc';
import { TranslateSvc } from '../services/TranslateSvc';
import { Utils } from '../underscore-extensions';
import { SubscriptionSvc } from '../settings/services/SubscriptionSvc';
import {
    SUBSCRIPTION_OPEN_MODAL,
    SubscriptionsParams
} from '../settings/directives/Subscriptions';

const plans = [
    'Guest',
    'Free',
    'WaveFree',
    'Starter',
    'Creator',
    'Pro',
    'Agency',
    'Business',
    'Corporate',
    'Ultimate'
]; // order make sense

export const getUpgradeButtonName = (translateSvc: TranslateSvc) => (
    planName: string
) => {
    const isUserLogged = !options.user.anonymous;
    const isFree = planName === 'Free';

    return !isUserLogged && isFree
        ? translateSvc.get('subscription.start-for-free')
        : `${translateSvc.get('get')} ${planName}`;
};

export class PricingSubscription {
    planName: string;
    private product: string = Utils.getProduct().toLowerCase();
    private settingsUrl = `${this.product}/pricing`;
    private subscriptionSale = {} as anm.SaleResponse;
    private generalButtonClasses = 'select-button button button-big button-red';
    private getUpgradeButtonName: (
        planName: string,
        isShort: boolean
    ) => string;

    constructor(
        private $scope: any,
        private $rootScope: ng.IRootScopeService,
        private $element: ng.IAugmentedJQuery,
        private $state: ng.ui.IStateService,
        private $stateParams: ng.ui.IStateParamsService,
        private $compile: ng.ICompileService,
        private $attrs: any,
        private AuthenticationSvc: AuthenticationSvc,
        private subscriptionSvc: SubscriptionSvc,
        private TranslateSvc: TranslateSvc
    ) {
        this.planName = $attrs.pricingSubscription;
        this.$scope.$watch('billing.period', () => this.setButtonsAndSale());
        this.$rootScope.$on(USER_UPDATE_SUCCESS, () => this.setButtons());
        this.getUpgradeButtonName = getUpgradeButtonName(TranslateSvc);
        this.setButtonsAndSale();
    }

    private setButtonsAndSale() {
        this.subscriptionSvc
            .getSale(this.getBillingPeriod())
            .then(sale => {
                this.subscriptionSale = sale;
            })
            .finally(() => {
                this.setButtons();
            });
    }

    private setButtons() {
        this.removePreviousButton();

        if (options.user.anonymous) {
            this.insertSignUpButton();
        } else {
            if (
                this.planName ===
                    this.subscriptionSvc.getDetails().displayName ||
                this.subscriptionSvc.isSameOldPlan(
                    this.getBillingPeriod(),
                    this.planName
                )
            ) {
                this.insertCurrentPlanButton();
            } else if (!this.isLowerPlan()) {
                this.insertUpgradeButton();
            }
        }
    }

    private removePreviousButton() {
        this.getButtonContainer(container => {
            const button = container.querySelector('.button');
            button && container.removeChild(button);
        });
    }

    private insertUpgradeButton() {
        const classes = `button-orange ${this.generalButtonClasses}`;

        const getOpenModalParamsWrapper = (action: anm.UpgradeAction) =>
            this.getOpenModalParams(
                action,
                this.planName,
                this.getBillingPeriod()
            );

        const insertButton = (action: anm.UpgradeAction) =>
            this.insertButtonElement(
                classes,
                `href="#" ng-click="PricingSubscription.openModal(${getOpenModalParamsWrapper(
                    action
                )})"`,
                this.getUpgradeButtonName(this.planName, options.handHeld)
            );

        if (this.isLowerPlan()) {
            insertButton('downgrade');
        } else {
            insertButton('upgrade');
        }
    }

    private isLowerPlan() {
        const currentPlan = this.subscriptionSvc.getDetails().displayName;
        return (
            !_.contains(plans, currentPlan) ||
            plans.indexOf(currentPlan) >= plans.indexOf(this.planName)
        );
    }

    private insertCurrentPlanButton() {
        const classes = `button-orange button-bordered ${this.generalButtonClasses}`;
        const {
            billingPeriod,
            displayName
        } = this.subscriptionSvc.getDetails();
        const period = this.getBillingPeriod();

        if (this.subscriptionSvc.isCurrentOldPlan()) {
            const params = this.getOpenModalParams(
                'switch-to-new',
                displayName,
                period
            );

            this.insertButtonElement(
                classes,
                `href="#" ng-click="PricingSubscription.openModal(${params})"`,
                this.getUpgradeButtonName(this.planName, options.handHeld)
            );

            return;
        }

        if (billingPeriod.toLowerCase() !== period && displayName !== 'Free') {
            const params = this.getOpenModalParams(
                'switch-period',
                displayName,
                period
            );

            this.insertButtonElement(
                classes,
                `href="#"  ng-click="PricingSubscription.openModal(${params})"`,
                `subscription.switch-to-${period}`
            );
        } else {
            this.insertButtonElement(
                `button-gray ${classes}`,
                ``,
                'wv.prcng.crnt-pln'
            );
        }
    }

    private insertSignUpButton() {
        this.insertButtonElement(
            `sign-up-now-btn ${this.generalButtonClasses}`,
            `href="#"  ng-click="PricingSubscription.openSignUp('${this.planName}')"`,
            this.getUpgradeButtonName(this.planName, options.handHeld)
        );
    }

    private getButtonContainer(cb: (container: Element) => void) {
        _.each(this.$element[0].querySelectorAll('.pricing-buttons'), cb);
    }

    private insertButtonElement(classes: string, attrs: string, title: string) {
        const titleText = this.TranslateSvc.has(title)
            ? this.TranslateSvc.get(title)
            : title;

        const compileButton = () =>
            this.$compile(`<a class="${classes}" ${attrs}>${titleText}</a>`)(
                this.$scope
            );

        this.getButtonContainer(container =>
            angular.element(container).append(compileButton())
        );
    }

    private getOpenModalParams(
        action: string,
        planName: string,
        period: string
    ) {
        const coupon =
            this.subscriptionSvc.isFree() && this.subscriptionSale.coupon
                ? `, coupon: '${this.subscriptionSale.coupon}'`
                : '';

        return `{modal: '${action}', plan: '${planName}', period: '${period}' ${coupon}}`;
    }

    private openModal(params: SubscriptionsParams) {
        this.$rootScope.$emit(SUBSCRIPTION_OPEN_MODAL, params);
    }

    private openSignUp(planName: string) {
        const url = this.getUrl(planName);
        this.AuthenticationSvc.openSignUpPage(url);
    }

    private getUrl(planName: string, action = 'upgrade') {
        const isCurrentFree = planName === 'Free';
        const params = Utils.toParams({
            plan: planName,
            period: this.getBillingPeriod(),
            ...(this.subscriptionSvc.isFree() &&
                this.subscriptionSale.coupon && {
                    coupon: this.subscriptionSale.coupon
                })
        });
        const dialogUrl = `/${action}?${params}`;

        return isCurrentFree
            ? ''
            : `${Utils.getWebsiteUrl()}pricing${dialogUrl}`;
    }

    private getBillingPeriod() {
        return this.$scope.billing.period.toLowerCase();
    }
}

angular
    .module('animatronApp.pricingSubscription', [])
    .directive('pricingSubscription', () => ({
        restrict: 'A',
        transclude: false,
        controller: PricingSubscription,
        controllerAs: 'PricingSubscription'
    }));



// WEBPACK FOOTER //
// ./app/assets/javascripts/directives/pricingSubscriptionDirective.ts